import React, { useState } from 'react';
import { api } from '../services/api';
import { Tenant } from '../types';

interface TenantCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (tenant: Tenant) => void;
}

interface FormData {
  organizationName: string;
  adminName: string;
  adminEmail: string;
  adminPhone: string;
  websiteUrl: string;
  maxAdvocates: number;
  maxClients: number;
}

export function TenantCreateModal({ isOpen, onClose, onCreated }: TenantCreateModalProps) {
  const [formData, setFormData] = useState<FormData>({
    organizationName: '',
    adminName: '',
    adminEmail: '',
    adminPhone: '',
    websiteUrl: '',
    maxAdvocates: 1,
    maxClients: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const tenant = await api.post<Tenant>('/admin/tenants', {
        organizationName: formData.organizationName,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        adminPhone: formData.adminPhone || undefined,
        websiteUrl: formData.websiteUrl || undefined,
        maxAdvocates: formData.maxAdvocates,
        maxClients: formData.maxClients,
      });
      onCreated(tenant);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      organizationName: '',
      adminName: '',
      adminEmail: '',
      adminPhone: '',
      websiteUrl: '',
      maxAdvocates: 1,
      maxClients: 10,
    });
    setError('');
    onClose();
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '4px',
    fontWeight: 500,
    fontSize: '14px',
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
          <h2 style={{ margin: 0 }}>Create New Tenant</h2>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Organization Name *</label>
            <input
              type="text"
              required
              value={formData.organizationName}
              onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
              style={inputStyle}
              placeholder="Acme Advocacy Services"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Admin Name *</label>
            <input
              type="text"
              required
              value={formData.adminName}
              onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
              style={inputStyle}
              placeholder="Jane Smith"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Admin Email *</label>
            <input
              type="email"
              required
              value={formData.adminEmail}
              onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
              style={inputStyle}
              placeholder="admin@example.com"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Admin Phone</label>
            <input
              type="tel"
              value={formData.adminPhone}
              onChange={(e) => setFormData({ ...formData, adminPhone: e.target.value })}
              style={inputStyle}
              placeholder="(555) 123-4567"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Website URL</label>
            <input
              type="url"
              value={formData.websiteUrl}
              onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
              style={inputStyle}
              placeholder="https://example.com"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Max Advocates</label>
              <input
                type="number"
                min="1"
                value={formData.maxAdvocates}
                onChange={(e) => setFormData({ ...formData, maxAdvocates: parseInt(e.target.value) || 1 })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Max Clients</label>
              <input
                type="number"
                min="1"
                value={formData.maxClients}
                onChange={(e) => setFormData({ ...formData, maxClients: parseInt(e.target.value) || 10 })}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #eee' }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1a237e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Creating...' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
